import React, { useState } from 'react';
import { Link, withRouter, RouteComponentProps } from 'react-router-dom';

// Import CSS styles
import './styles/RecipeTemplate.css';
import './styles/BlogDetailTemplate.css';

interface BlogDetailTemplateProps extends RouteComponentProps {
  title: string;
  publishedAt: string;
  author?: { name: string };
  image?: { url: string; altText?: string };
  excerpt?: string;
  contentHtml: string;
  tags?: string[];
  backLink?: string;
  backLinkText?: string;
  latestPosts: Array<{
    id: string;
    handle: string;
    title: string;
    publishedAt: string;
    tags?: string[];
    image?: { url: string; altText?: string };
  }>;
}

const BlogDetailTemplate: React.FC<BlogDetailTemplateProps> = ({
  title,
  publishedAt,
  author,
  image,
  excerpt,
  contentHtml,
  tags,
  backLink = '/browse/blogs',
  backLinkText = 'Back to Blogs',
  latestPosts,
  history
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  
  // Function to handle search submission
  const handleSearch = () => {
    if (searchTerm.trim()) {
      // Navigate to blogs page with search parameter
      history.push(`/browse/blogs?search=${encodeURIComponent(searchTerm)}`);
    }
  };
  
  // Handle Enter key press
  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };
  
  const date = new Date(publishedAt).toLocaleDateString(undefined, { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
  
  return (
    <div className="blog-container">
      <div className="blog-content-wrapper">
        <Link to={backLink} className="blog-back-link">
          &larr; {backLinkText}
        </Link>
        
        <div className="blog-layout">
        {/* Main Content */}
        <div className="blog-main">
          {image && (
            <div className="blog-hero">
              <img 
                src={image.url} 
                alt={image.altText || title}
                className="blog-featured-image"
              />
            </div>
          )}
          
          <div className="blog-header">
            <h1 className="blog-title">{title}</h1>
            
            <div className="blog-meta">
              <span className="blog-date">
                {/* Calendar Icon */}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
                <span style={{ marginLeft: '5px' }}>{date}</span>
              </span>
              {/* Replace author with Admin */}
              <span className="blog-author">
                {/* User Icon */}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
                <span style={{ marginLeft: '5px' }}>By Admin</span>
              </span>

            </div>
            
            {excerpt && (
              <div className="blog-intro">
                {excerpt}
              </div>
            )}
          </div>
          
          <div className="blog-content">
            <div dangerouslySetInnerHTML={{ __html: contentHtml }} />
          </div>
          
          {tags && tags.length > 0 && (
            <div className="blog-tags">
              <h2 className="sidebar-heading">Tags</h2>
              <div className="tags-container">
                {tags.map((tag, index) => (
                  <span 
                    key={index}
                    className="blog-tag"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
        
        {/* Sidebar */}
        <div className="blog-sidebar">
          {/* Search Bar */}
          <div className="sidebar-section">
            <h2 className="sidebar-heading">Search</h2>
            <div className="search-container">
              <input
                type="text"
                placeholder="Search Here..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={handleKeyPress}
                className="search-input"
              />
              <button
                className="search-button"
                onClick={handleSearch}
                style={{
                  backgroundColor: '#9300DA',
                  border: 'none',
                  borderRadius: '0 4px 4px 0',
                  cursor: 'pointer',
                  padding: '0 12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <svg 
                  width="18" 
                  height="18" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="white" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
              </button>
            </div>
          </div>
          
          {/* Latest Posts */}
          <div className="sidebar-section">
            <h2 className="sidebar-heading">Latest Post</h2>
            
            <div className="latest-posts-container">
              {latestPosts.map(post => (
                <div key={post.id} className="latest-post-item">
                  {post.image ? (
                    <img 
                      src={post.image.url} 
                      alt={post.title}
                      className="post-thumbnail"
                    />
                  ) : (
                    <div className="post-thumbnail-placeholder">
                      No Image
                    </div>
                  )}
                  
                  <div className="post-details">
                    <Link 
                      to={`/browse/blogs/${post.handle}`}
                      className="post-title-link"
                    >
                      {post.title}
                    </Link>
                    <div className="post-meta">
                      {post.tags && post.tags.length > 0 ? post.tags[0] : 'Blog'} - {new Date(post.publishedAt).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default withRouter(BlogDetailTemplate);
